import { ApiInstance } from '@/lib/api'
import type { MetricSeries, Place } from '@/lib/dashboard'
import type { OverdueBucket } from '@/lib/overdueAging'

export interface PeriodQuery {
  from: string
  to: string
  tz: string
}

export interface TopRealState {
  realStateId: string
  views: number
}

export interface TopPlace extends Place {
  sessions: number
}

export interface PeriodTotals {
  total: number
  previousTotal: number
}

export interface SiteTraffic {
  from: string
  to: string
  tz: string
  sessions: MetricSeries
  visitors: PeriodTotals
  viewingSessions?: PeriodTotals
  realStateViews: MetricSeries
  topRealStates: TopRealState[]
  topPlaces: TopPlace[]
}

export interface RecentVisit extends Place {
  sessionId: string
  latitude: number
  longitude: number
  lastSeenAt: string
  live: boolean
  lastEventType?: string | null
  realStateId?: string | null
}

export interface VisitorPlace extends Place {
  latitude: number
  longitude: number
  sessions: number
}

export interface SiteVisitors {
  live: number
  recent: RecentVisit[]
  places: VisitorPlace[]
}

export interface LeadStats {
  leads: MetricSeries
  awaiting: number
  open: number
  stagnant?: number
}

export interface LeadStatsQuery extends PeriodQuery {
  funnelId?: string
  assigneeId?: string
  channel?: string
}

export interface MoneyTotal {
  count: number
  amount: number
}

export interface PaymentDelinquency {
  dueAmount: number
  overdueAmount: number
  rate: number | null
}

export interface EndingContract {
  id: string
  realStateId: string | null
  tenantId: string | null
  endDate: string
}

export interface PaymentStats {
  received: MetricSeries
  expected?: MetricSeries
  toReceive?: MoneyTotal
  overdue: MoneyTotal & { buckets?: OverdueBucket[] }
  delinquency?: PaymentDelinquency
  online?: MoneyTotal
  manual?: MoneyTotal
  dueSoon: MoneyTotal
  activeContracts: number
  endingContracts?: { count: number; rows: EndingContract[] }
}

export const DASHBOARD_QUERY_KEY = 'dashboard'

export async function fetchSiteTraffic(period: PeriodQuery): Promise<SiteTraffic> {
  const { data } = await ApiInstance.get<SiteTraffic>('dashboard/traffic', { params: period })
  return data
}

export async function fetchSiteVisitors(): Promise<SiteVisitors> {
  const { data } = await ApiInstance.get<SiteVisitors>('dashboard/visitors')
  return data
}

export async function fetchLeadStats(query: LeadStatsQuery): Promise<LeadStats> {
  const { data } = await ApiInstance.get<LeadStats>('crm/sales-funnel/stats', { params: query })
  return data
}

export async function fetchPaymentStats(period: PeriodQuery): Promise<PaymentStats> {
  const { data } = await ApiInstance.get<PaymentStats>('rentals/payments/stats', { params: period })
  return data
}
